import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import type { LucideIcon } from 'lucide-react';
import {
  CheckCircle,
  ChevronDown,
  ChevronUp,
  FileText,
  Gauge,
  LineChart,
  Search,
} from 'lucide-react';

import PixelBackground from '@/components/PixelBackground';
import { Header } from '@/components/Header';
import { Footer } from '@/components/Footer';
import Seo from '@/components/Seo';
import { JsonLd } from '@/components/JsonLd';
import Display from '@/components/typography/Display';
import Button from '@/components/ui/Button';
import { Container, HeroSection, Section } from '@/components/ui/Section';
import { useLanguage } from '@/contexts/LanguageContext';
import { useLocalizedPath, stripLanguagePrefix, withLanguagePrefix } from '@/lib/locale';
import { BASE_URL, getHreflangLinks } from '@/seo/meta';
import {
  generateBreadcrumbSchema,
  generateFAQSchema,
  generateOrganizationSchema,
  generateServiceSchema,
  generateWebsiteSchema,
} from '@/lib/seo';
import { trackCTA, trackServiceView } from '@/lib/analytics';
import { trackScroll } from '@/lib/scroll';
import { getServiceMeta } from '@/seo/meta';

const serviceName = 'Υπηρεσίες SEO';
const serviceSlug = 'seo';

type Locale = 'en' | 'el';

type Metric = {
  value: string;
  label: string;
};

type HeroContent = {
  eyebrow: string;
  leading: string;
  description: string;
  metrics: Metric[];
  primaryCta: string;
  secondaryCta: string;
};

type PillarKey = 'technical' | 'content' | 'structure' | 'measurement';

type PillarCopy = {
  title: string;
  description: string;
  bullets: string[];
};

type ProcessStep = {
  title: string;
  description: string;
  duration?: string;
  bullets?: string[];
};

type FaqItem = {
  question: string;
  answer: string;
};

type FaqIntroContent = {
  eyebrow: string;
  heading: string;
  description: string;
};

type ClosingContent = {
  eyebrow: string;
  title: string;
  description: string;
  highlights: string[];
  primaryCta: string;
  secondaryCta: string;
};

const heroCopy: Record<Locale, HeroContent> = {
  en: {
    eyebrow: 'SEO Services',
    leading: 'SEO that brings organic traffic and delivers results.',
    description:
      'We improve your site’s technical foundation, content, and structure so it ranks higher on Google and turns visitors into customers.',
    metrics: [
      { value: 'From 4–8 weeks', label: 'First measurable improvements' },
      { value: 'Full SEO audit', label: 'Technical issues & content opportunities' },
      { value: 'Continuous improvement', label: 'Prioritized actions and reporting' },
    ],
    primaryCta: 'Book a free consultation',
    secondaryCta: 'See our work',
  },
  el: {
    eyebrow: 'Υπηρεσίες SEO',
    leading: 'SEO που φέρνει οργανική επισκεψιμότητα και φέρνει αποτέλεσμα.',
    description:
      'Βελτιώνουμε την τεχνική βάση, το περιεχόμενο και τη δομή της ιστοσελίδας σας, ώστε να εμφανίζεται ψηλότερα στο Google και να μετατρέπει τους επισκέπτες σε πελάτες.',
    metrics: [
      { value: 'Από 4–8 εβδομάδες', label: 'Πρώτες μετρήσιμες βελτιώσεις' },
      { value: 'Πλήρης έλεγχος SEO', label: 'Τεχνικά προβλήματα & ευκαιρίες περιεχομένου' },
      { value: 'Συνεχής βελτίωση', label: 'Ιεράρχηση ενεργειών & αναφορές' },
    ],
    primaryCta: 'Κλείστε δωρεάν συμβουλευτική',
    secondaryCta: 'Δείτε έργα μας',
  },
};

const approachCopy: Record<Locale, { eyebrow: string; heading: string; description: string }> = {
  en: {
    eyebrow: 'What SEO includes',
    heading: 'SEO depends on technical health, structure, and the right content.',
    description:
      'We build strong foundations, organize pages, and improve content so it answers real customer searches.',
  },
  el: {
    eyebrow: 'Τι περιλαμβάνει το SEO',
    heading: 'Το SEO βασίζεται στην τεχνική κατάσταση της ιστοσελίδας, στη σωστή δομή και στο κατάλληλο περιεχόμενο.',
    description:
      'Δημιουργούμε γερές βάσεις, οργανώνουμε τις σελίδες και βελτιώνουμε το περιεχόμενο ώστε να απαντά σε πραγματικές αναζητήσεις πελατών.',
  },
};

const pillarBase: Array<{ key: PillarKey; icon: LucideIcon }> = [
  { key: 'technical', icon: Gauge },
  { key: 'content', icon: FileText },
  { key: 'structure', icon: Search },
  { key: 'measurement', icon: LineChart },
] as const;

const pillarCopy: Record<Locale, Record<PillarKey, PillarCopy>> = {
  en: {
    technical: {
      title: 'Website technical foundation',
      description: 'We make sure your site is fast, functional, and free of technical issues.',
      bullets: [
        'Speed checks and correct appearance in Google',
        'Performance improvements and technical fixes',
        'Proper page structure and unique URLs',
      ],
    },
    content: {
      title: 'Content & customer searches',
      description: 'We align content with what your customers actually search for.',
      bullets: [
        'Keyword research based on audience needs',
        'Topics that answer common questions',
        'Content guidelines for consistent production',
      ],
    },
    structure: {
      title: 'Page structure & user experience',
      description: 'We make the site clearer and easier to use.',
      bullets: [
        'Improved titles and headings',
        'Proper internal linking between pages',
        'Improvements for better mobile experience',
      ],
    },
    measurement: {
      title: 'Measurement & continuous improvement',
      description: 'We track progress and organize the next steps.',
      bullets: [
        'Google Analytics and Search Console setup',
        'Monthly performance reporting',
        'Prioritized list of actions',
      ],
    },
  },
  el: {
    technical: {
      title: 'Τεχνική βάση ιστοσελίδας',
      description: 'Φροντίζουμε η ιστοσελίδα να είναι γρήγορη, λειτουργική και χωρίς τεχνικά προβλήματα.',
      bullets: [
        'Έλεγχος ταχύτητας και σωστής εμφάνισης στο Google',
        'Βελτιώσεις απόδοσης και τεχνικές διορθώσεις',
        'Σωστή δομή σελίδων και μοναδικές διευθύνσεις',
      ],
    },
    content: {
      title: 'Περιεχόμενο & αναζητήσεις πελατών',
      description: 'Προσαρμόζουμε το περιεχόμενο σε όσα πραγματικά ψάχνουν οι πελάτες σας.',
      bullets: [
        'Έρευνα λέξεων-κλειδιών με βάση τις ανάγκες του κοινού',
        'Θεματολογία που απαντά σε συχνές ερωτήσεις',
        'Οδηγίες περιεχομένου για σταθερή παραγωγή',
      ],
    },
    structure: {
      title: 'Δομή σελίδων & εμπειρία χρήστη',
      description: 'Κάνουμε την ιστοσελίδα πιο ξεκάθαρη και πιο εύκολη στη χρήση.',
      bullets: [
        'Βελτιωμένοι τίτλοι και επικεφαλίδες',
        'Σωστή σύνδεση των σελίδων μεταξύ τους',
        'Βελτιώσεις για καλύτερη εμπειρία σε κινητά',
      ],
    },
    measurement: {
      title: 'Μέτρηση & συνεχής βελτίωση',
      description: 'Παρακολουθούμε την πρόοδο και οργανώνουμε τα επόμενα βήματα.',
      bullets: [
        'Ρυθμίσεις Google Analytics και Search Console',
        'Μηνιαία αναφορά αποτελεσμάτων',
        'Λίστα ενεργειών με σειρά προτεραιότητας',
      ],
    },
  },
};

const processIntro: Record<Locale, { eyebrow: string; heading: string; description: string }> = {
  en: {
    eyebrow: 'How the SEO project runs',
    heading: 'Clear steps and measurable progress.',
    description: 'We start with a review, define a plan, and improve continuously based on data.',
  },
  el: {
    eyebrow: 'Πώς προχωρά το SEO',
    heading: 'Ξεκάθαρα βήματα και μετρήσιμη πρόοδος.',
    description: 'Ξεκινάμε με έλεγχο, ορίζουμε πλάνο και βελτιώνουμε συνεχώς με βάση τα στοιχεία.',
  },
};

const processCopy: Record<Locale, ProcessStep[]> = {
  en: [
    {
      title: 'Review & goals',
      duration: 'Week 1',
      description: 'We assess the current state and set priorities.',
      bullets: [
        'Technical review of the website',
        'Competitive review',
        'Goal and measurement definition',
      ],
    },
    {
      title: 'Strategy & action plan',
      duration: 'Weeks 2–3',
      description: 'We define keywords and content structure.',
      bullets: [
        'Keyword research',
        'Content and page organization',
        'Prioritized action list',
      ],
    },
    {
      title: 'Implementation & improvements',
      duration: 'Weeks 3–6',
      description: 'We apply technical fixes and improve content.',
      bullets: [
        'Page and content improvements',
        'Creation or improvement of key pages',
        'Final checks before publication',
      ],
    },
    {
      title: 'Monitoring & growth',
      duration: 'Ongoing',
      description: 'We measure, report, and improve every month.',
      bullets: [
        'Reports from Google Search Console and Analytics',
        'Improvements based on real data',
        'Continuous improvement plan',
      ],
    },
  ],
  el: [
    {
      title: 'Έλεγχος & στόχοι',
      duration: 'Εβδομάδα 1',
      description: 'Εξετάζουμε την τρέχουσα κατάσταση και ορίζουμε προτεραιότητες.',
      bullets: [
        'Τεχνικός έλεγχος της ιστοσελίδας',
        'Έλεγχος ανταγωνισμού',
        'Καθορισμός στόχων και μετρήσεων',
      ],
    },
    {
      title: 'Στρατηγική & πλάνο ενεργειών',
      duration: 'Εβδομάδες 2–3',
      description: 'Ορίζουμε λέξεις-κλειδιά και δομή περιεχομένου.',
      bullets: [
        'Έρευνα λέξεων-κλειδιών',
        'Οργάνωση θεματολογίας και σελίδων',
        'Λίστα ενεργειών με σειρά προτεραιότητας',
      ],
    },
    {
      title: 'Εφαρμογή & βελτιώσεις',
      duration: 'Εβδομάδες 3–6',
      description: 'Υλοποιούμε τις τεχνικές διορθώσεις και βελτιώνουμε το περιεχόμενο.',
      bullets: [
        'Βελτιώσεις στις σελίδες και στο περιεχόμενο',
        'Δημιουργία ή βελτίωση βασικών σελίδων',
        'Τελικός έλεγχος πριν τη δημοσίευση',
      ],
    },
    {
      title: 'Παρακολούθηση & εξέλιξη',
      duration: 'Συνεχής',
      description: 'Μετράμε, ενημερώνουμε και βελτιώνουμε κάθε μήνα.',
      bullets: [
        'Αναφορές από Google Search Console και Analytics',
        'Βελτιώσεις με βάση πραγματικά δεδομένα',
        'Συνεχές πλάνο βελτίωσης',
      ],
    },
  ],
};

const faqCopy: Record<Locale, FaqItem[]> = {
  en: [
    {
      question: 'When will we see SEO results?',
      answer:
        'Typically you see early signals in 4–8 weeks and more stable growth in 3–6 months, depending on competition and starting point.',
    },
    {
      question: 'Do we need new content?',
      answer:
        'Usually yes. We improve existing pages and create new ones to cover the searches your audience makes.',
    },
    {
      question: 'Do you handle technical fixes?',
      answer:
        'Yes. We fix errors, speed issues, crawlability, and indexation so the site performs correctly.',
    },
    {
      question: 'Will we get reports?',
      answer:
        'Yes. We provide regular reports with KPIs, changes, and next priorities.',
    },
    {
      question: 'Can you work on an existing website?',
      answer:
        'Absolutely. We start with an audit and adapt the plan to your current website.',
    },
    {
      question: 'Is SEO a one-time project?',
      answer:
        'SEO works best as an ongoing process. We can start with a foundation phase and continue with monthly optimization.',
    },
  ],
  el: [
    {
      question: 'Πότε φαίνονται τα αποτελέσματα του SEO;',
      answer:
        'Συνήθως υπάρχουν πρώτες ενδείξεις σε 4–8 εβδομάδες και πιο σταθερή άνοδος σε 3–6 μήνες, ανάλογα με τον ανταγωνισμό.',
    },
    {
      question: 'Χρειάζεται να γράψουμε νέο περιεχόμενο;',
      answer:
        'Συνήθως ναι. Βελτιώνουμε υπάρχουσες σελίδες και δημιουργούμε νέες για να καλύψουμε τις αναζητήσεις του κοινού σας.',
    },
    {
      question: 'Αναλαμβάνετε τεχνικές διορθώσεις στο site;',
      answer:
        'Ναι. Διορθώνουμε σφάλματα, ταχύτητα, crawlability και θέματα indexation ώστε το site να λειτουργεί σωστά.',
    },
    {
      question: 'Θα έχουμε αναφορές;',
      answer:
        'Ναι. Παρέχουμε τακτικές αναφορές με KPIs, αλλαγές και επόμενες προτεραιότητες.',
    },
    {
      question: 'Μπορείτε να δουλέψετε σε υπάρχουσα ιστοσελίδα;',
      answer:
        'Φυσικά. Ξεκινάμε με audit και προσαρμόζουμε το πλάνο στο υπάρχον site.',
    },
    {
      question: 'Το SEO είναι κάτι που γίνεται μία φορά;',
      answer:
        'Το SEO δουλεύει καλύτερα ως συνεχής διαδικασία. Ξεκινάμε με σταθερή βάση και συνεχίζουμε με μηνιαία βελτιστοποίηση.',
    },
  ],
};

const faqIntroCopy: Record<Locale, FaqIntroContent> = {
  en: {
    eyebrow: 'FAQ',
    heading: 'Frequently asked questions',
    description: 'Everything you need to know before starting.',
  },
  el: {
    eyebrow: 'FAQ',
    heading: 'Συχνές ερωτήσεις',
    description: 'Όσα χρειάζεται να γνωρίζετε πριν ξεκινήσετε.',
  },
};

const closingCopy: Record<Locale, ClosingContent> = {
  en: {
    eyebrow: 'Ready to start?',
    title: 'Improve your organic visibility.',
    description: 'We review your site and propose the next best steps for growth.',
    highlights: [],
    primaryCta: 'Book a free consultation',
    secondaryCta: 'See our services',
  },
  el: {
    eyebrow: 'Έτοιμοι να ξεκινήσουμε;',
    title: 'Να βελτιώσουμε την οργανική σας παρουσία;',
    description: 'Εξετάζουμε το site σας και προτείνουμε τα επόμενα βήματα για ανάπτυξη.',
    highlights: [],
    primaryCta: 'Κλείστε δωρεάν συμβουλευτική',
    secondaryCta: 'Δείτε τις υπηρεσίες μας',
  },
};

export default function SeoService() {
  const { language } = useLanguage();
  const location = useLocation();
  const locale: Locale = language === 'el' ? 'el' : 'en';
  const localizedPath = useLocalizedPath();

  const metaData = getServiceMeta(serviceSlug, locale);
  const canonicalPath = withLanguagePrefix(stripLanguagePrefix(location.pathname), locale);
  const canonicalUrl = `${BASE_URL}${canonicalPath}`;
  const hreflang = getHreflangLinks(location.pathname, locale);
  const serviceDisplayName = locale === 'en' ? 'SEO Services' : serviceName;

  const hero = heroCopy[locale];
  const approach = approachCopy[locale];
  const pillars = pillarCopy[locale];
  const processIntroCopy = processIntro[locale];
  const processSteps = processCopy[locale];
  const faqItems = faqCopy[locale];
  const faqIntro = faqIntroCopy[locale];
  const closing = closingCopy[locale];

  const [openFaqIndex, setOpenFaqIndex] = useState<number | null>(null);

  useEffect(() => {
    trackServiceView(serviceSlug, language);
    const cleanup = trackScroll({ threshold: 0.75, once: true });
    return cleanup;
  }, [language]);

  const handleCTA = (ctaText: string) => {
    trackCTA(ctaText, window.location.pathname);
    window.navigateToConsultation?.();
  };

  const toggleFaq = (index: number) => {
    setOpenFaqIndex((current) => (current === index ? null : index));
  };

  return (
    <>
      <Seo
        title={metaData.title}
        description={metaData.description}
        url={canonicalUrl}
        image={metaData.image}
        hreflang={hreflang}
      />

      <JsonLd json={generateOrganizationSchema()} />
      <JsonLd json={generateWebsiteSchema(locale)} />
      <JsonLd json={generateBreadcrumbSchema(serviceDisplayName, serviceSlug, locale)} />
      <JsonLd json={generateServiceSchema(serviceDisplayName, metaData.description, canonicalUrl, 'SEO')} />
      <JsonLd json={generateFAQSchema(faqItems)} />

      <div className="relative min-h-screen bg-[#0b0a0e] text-white">
        <PixelBackground />
        <Header variant="simple" />

        <main id="main-content" className="relative z-10 overflow-hidden">
          <HeroSection className="relative overflow-hidden pt-32 pb-20 md:pt-36 lg:pt-40 lg:pb-24">
            <div className="absolute inset-0 geometric-pattern" aria-hidden="true" />
            <div
              className="absolute inset-0 bg-gradient-to-br from-pp-black/95 via-black/85 to-black"
              aria-hidden="true"
            />
            <Container className="relative lg:min-h-[calc(100vh-220px)] lg:flex lg:flex-col lg:justify-center">
              <div className="max-w-3xl space-y-8">
                <div className="space-y-5">
                  <p className="text-[11px] font-medium uppercase tracking-[0.4em] text-brand-highlight/70">
                    {hero.eyebrow}
                  </p>
                  <Display
                    variant="h1"
                    as="h1"
                    className="text-white text-3xl sm:text-4xl md:text-5xl lg:text-6xl leading-[1.12] tracking-tight"
                  >
                    {hero.leading}
                  </Display>
                </div>

                <Display
                  variant="subhead"
                  as="p"
                  className="max-w-2xl text-base sm:text-lg leading-relaxed text-white/70"
                >
                  {hero.description}
                </Display>

                <div className="flex flex-col gap-4 sm:flex-row">
                  <Button
                    variant="primary"
                    size="lg"
                    className="w-full justify-center sm:w-auto"
                    onClick={() => handleCTA(hero.primaryCta)}
                  >
                    {hero.primaryCta}
                  </Button>
                  <Button
                    as="a"
                    href={localizedPath('/projects')}
                    variant="secondary"
                    size="lg"
                    className="w-full justify-center sm:w-auto"
                  >
                    {hero.secondaryCta}
                  </Button>
                </div>
              </div>

              <div className="mt-10 grid gap-6 border-t border-white/10 pt-8 sm:grid-cols-3 sm:gap-0 sm:divide-x sm:divide-white/10">
                {hero.metrics.map((metric) => (
                  <div key={metric.label} className="space-y-1 py-2 sm:px-6">
                    <p className="text-base font-semibold tracking-tight text-white md:text-base">{metric.value}</p>
                    <p className="text-[11px] uppercase tracking-[0.2em] text-white/55">{metric.label}</p>
                  </div>
                ))}
              </div>
            </Container>
          </HeroSection>

          <Section>
            <Container>
              <div className="max-w-3xl space-y-4">
                <p className="text-xs font-semibold uppercase tracking-[0.3em] text-brand-highlight/80">
                  {approach.eyebrow}
                </p>
                <h2 className="text-3xl font-semibold text-white md:text-5xl">{approach.heading}</h2>
                <p className="text-lg text-white/70">{approach.description}</p>
              </div>

              <div className="mt-12 grid gap-8 md:grid-cols-2">
                {pillarBase.map(({ key, icon: Icon }) => {
                  const copy = pillars[key];
                  return (
                    <div
                      key={key}
                      className="group relative overflow-hidden rounded-2xl border border-white/15 bg-black/70 backdrop-blur-xl p-8 shadow-glass transition hover:bg-white/10 hover:border-white/25 hover:shadow-lg hover:shadow-white/10"
                    >
                      <div className="flex h-12 w-12 items-center justify-center rounded-full border border-white/10 bg-white/5 text-brand-highlight/80 transition group-hover:border-brand-highlight/60 group-hover:text-brand-highlight">
                        <Icon className="h-6 w-6" />
                      </div>
                      <h3 className="mt-6 text-2xl font-semibold text-white">{copy.title}</h3>
                      <p className="mt-3 text-sm text-white/70">{copy.description}</p>
                      <ul className="mt-6 space-y-3 text-sm text-white/75">
                        {copy.bullets.map((bullet) => (
                          <li key={bullet} className="flex items-start gap-3">
                            <CheckCircle className="mt-0.5 h-4 w-4 text-brand-highlight/70" />
                            <span className="leading-relaxed">{bullet}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  );
                })}
              </div>
            </Container>
          </Section>

          <Section id="process" className="relative isolate">
            <div className="absolute inset-0 geometric-pattern opacity-20" aria-hidden="true" />
            <Container className="relative">
              <div className="max-w-3xl space-y-4 text-center md:mx-auto">
                <p className="text-xs font-semibold uppercase tracking-[0.3em] text-brand-highlight/80">
                  {processIntroCopy.eyebrow}
                </p>
                <h2 className="text-3xl font-semibold text-white md:text-5xl">{processIntroCopy.heading}</h2>
                <p className="text-lg text-white/70">{processIntroCopy.description}</p>
              </div>

              <div className="mt-16 grid gap-8 lg:grid-cols-2">
                {processSteps.map((step, index) => (
                  <div
                    key={step.title}
                    className="group flex h-full flex-col gap-4 rounded-2xl border border-white/15 bg-black/70 backdrop-blur-xl p-8 shadow-glass transition hover:bg-white/10 hover:border-white/25 hover:shadow-lg hover:shadow-white/10"
                  >
                    <div className="flex items-center gap-4">
                      <span className="flex h-12 w-12 items-center justify-center rounded-full border border-white/10 bg-white/5 text-lg font-semibold text-white transition group-hover:border-brand-highlight/60 group-hover:text-brand-highlight">
                        {(index + 1).toString().padStart(2, '0')}
                      </span>
                      <div>
                        {step.duration ? (
                          <p className="text-xs uppercase tracking-[0.3em] text-white/50">{step.duration}</p>
                        ) : null}
                        <h3 className={step.duration ? 'mt-1 text-2xl font-semibold text-white' : 'text-2xl font-semibold text-white'}>
                          {step.title}
                        </h3>
                      </div>
                    </div>
                    <p className="text-sm text-white/70">{step.description}</p>
                    {step.bullets?.length ? (
                      <ul className="mt-4 space-y-2 text-sm text-white/70">
                        {step.bullets.map((item) => (
                          <li key={item} className="flex items-start gap-2">
                            <span className="mt-1 h-1.5 w-1.5 rounded-full bg-brand-highlight/70" />
                            <span>{item}</span>
                          </li>
                        ))}
                      </ul>
                    ) : null}
                  </div>
                ))}
              </div>
            </Container>
          </Section>

          <Section>
            <Container className="max-w-5xl">
              <div className="text-center">
                <p className="text-xs font-semibold uppercase tracking-[0.3em] text-brand-highlight/80">
                  {faqIntro.eyebrow}
                </p>
                <h2 className="mt-4 text-3xl font-semibold text-white md:text-5xl">
                  {faqIntro.heading}
                </h2>
                <p className="mt-4 text-lg text-white/70">{faqIntro.description}</p>
              </div>

              <div className="mt-12 space-y-4">
                {faqItems.map((faq, index) => {
                  const open = openFaqIndex === index;
                  return (
                    <div
                      key={faq.question}
                      className="overflow-hidden rounded-2xl border border-white/15 bg-black/70 backdrop-blur-xl transition hover:bg-white/10 hover:border-white/25 hover:shadow-lg hover:shadow-white/10"
                    >
                      <button
                        type="button"
                        onClick={() => toggleFaq(index)}
                        className="flex w-full items-center justify-between gap-4 px-6 py-5 text-left text-base font-semibold text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white/40"
                      >
                        <span>{faq.question}</span>
                        {open ? (
                          <ChevronUp className="h-5 w-5 text-white/60" />
                        ) : (
                          <ChevronDown className="h-5 w-5 text-white/60" />
                        )}
                      </button>
                      {open && (
                        <div className="border-t border-white/10 px-6 py-5 text-sm text-white/70">
                          <p className="leading-relaxed">{faq.answer}</p>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </Container>
          </Section>

          <Section className="pt-0">
            <Container>
              <div className="relative overflow-hidden rounded-[32px] border border-white/15 bg-black/70 p-12 shadow-glass">
                <div className="pointer-events-none absolute -right-24 -top-32 h-72 w-72 rounded-full bg-brand-highlight/30 blur-3xl opacity-60" />
                <div className="pointer-events-none absolute -left-24 bottom-0 h-64 w-64 rounded-full bg-white/10 blur-3xl opacity-60" />
                <div className="relative">
                  <div className="mb-8 flex flex-col gap-3 rounded-2xl border border-white/10 bg-black/60 px-8 py-6 text-center">
                    <p className="text-xs font-semibold uppercase tracking-[0.35em] text-brand-highlight/80">
                      {closing.eyebrow}
                    </p>
                    <Display
                      variant="h1"
                      as="h2"
                      className="mx-auto max-w-4xl text-balance text-xl font-semibold text-white md:text-3xl"
                    >
                      {closing.title}
                    </Display>
                  </div>

                  <p className="mx-auto max-w-3xl text-center text-lg text-white/70">{closing.description}</p>

                  {closing.highlights.length ? (
                    <ul className="mx-auto mt-8 grid gap-3 text-left text-sm text-white/70 sm:grid-cols-3 sm:gap-4">
                      {closing.highlights.map((highlight) => (
                        <li
                          key={highlight}
                          className="group flex items-start gap-3 rounded-2xl border border-white/5 bg-black/60 px-5 py-4 transition hover:border-white/15"
                        >
                          <span className="mt-1 h-2 w-2 rounded-full bg-brand-highlight/70 transition-colors duration-300 group-hover:bg-brand-highlight" />
                          <span className="leading-relaxed">{highlight}</span>
                        </li>
                      ))}
                    </ul>
                  ) : null}

                  <div className="mt-12 flex flex-col gap-4 text-center sm:flex-row sm:justify-center">
                    <Button
                      variant="primary"
                      size="lg"
                      className="w-full justify-center sm:w-auto"
                      onClick={() => handleCTA(closing.primaryCta)}
                    >
                      {closing.primaryCta}
                    </Button>
                    <Button
                      as="a"
                      href={localizedPath('/services')}
                      variant="secondary"
                      size="lg"
                      className="w-full justify-center sm:w-auto"
                    >
                      {closing.secondaryCta}
                    </Button>
                  </div>
                </div>
              </div>
            </Container>
          </Section>
        </main>

        <Footer variant="compact" />
      </div>
    </>
  );
}
